import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BackButton, MultiSelectFilter, Pagination, DataTable, SearchFilterBar, Modal, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../components/ScopedUserSearchFilter';
import { REGISTRABLE_PROGRAMME_OPTIONS } from '@padcanvas/shared';
import { fetchWithAdmin } from '../api/fetchHelper';
import { authApi } from '../api/auth';
import { TipPreviewModal, hasPreview } from './whats-new/TipPreviewModal';
import { SurveyBuilder } from './whats-new/SurveyBuilder';
import { SurveyResults } from './whats-new/SurveyResults';
import type { SurveyDefinition, SurveyResults as SurveyResultsData } from '@shared-ui/whats-new';
import './WhatsNewTips.css';

// ── Types ────────────────────────────────────────────────────────────────

/** Stored values. `none` is a UI-only transient — unticking every box — that
 *  the save button refuses, so it never reaches the API. */
type AppTarget = 'student' | 'teacher' | 'all' | 'selected' | 'none';

/** The audience choices, as ticked. Students/Teachers combine ('all'); Selected
 *  users is exclusive of both — it turns the criteria off entirely and takes its
 *  audience from the Audience tab instead. */
const APP_TARGETS: { value: 'student' | 'teacher' | 'selected'; label: string }[] = [
  { value: 'student', label: 'Students' },
  { value: 'teacher', label: 'Teachers' },
  { value: 'selected', label: 'Selected Users' },
];

/** Is this choice covered by the stored target? `all` covers both apps. */
const appTargetIncludes = (target: AppTarget, app: 'student' | 'teacher' | 'selected'): boolean =>
  app === 'selected' ? target === 'selected' : target === 'all' || target === app;

/** Ticking/unticking one choice, folded back into the stored values. */
function toggleAppTarget(target: AppTarget, app: 'student' | 'teacher' | 'selected'): AppTarget {
  // Selected Users is its own audience, not a third app: it can't be combined
  // with either, so ticking it clears them and unticking it leaves nothing.
  if (app === 'selected') return target === 'selected' ? 'none' : 'selected';
  if (target === 'selected') return app;
  const other = app === 'student' ? 'teacher' : 'student';
  const has = appTargetIncludes(target, app);
  const hasOther = appTargetIncludes(target, other);
  const next = { [app]: !has, [other]: hasOther } as Record<'student' | 'teacher', boolean>;
  if (next.student && next.teacher) return 'all';
  if (next.student) return 'student';
  if (next.teacher) return 'teacher';
  return 'none';
}
type Platform = 'ios' | 'android' | 'web';
type Ring = 'devtesting' | 'beta' | 'prod' | 'guest';
type ImageFit = 'contain' | 'full-bleed';

/** Which controls the card carries. 'default' = the Dismiss + Remind-me-later
 *  footer; 'close-only' = no footer, the top-right ✕ is the single control and
 *  it dismisses. Absent on cards written before the field existed. */
type Presentation = 'default' | 'close-only';

interface PlatformOverride {
  title?: string;
  body?: string;
  imageUrl?: string;
}

const SUBJECTS = ['maths', 'physics', 'chemistry', 'biology'] as const;
type Subject = (typeof SUBJECTS)[number];

interface SubjectSolveCondition {
  type: 'subject-daily-solve';
  targetSubject: string;
  targetMinAttempted: number;
  excludeSubject: string;
  excludeMaxAttempted: number;
  windowDays: number;
}

/** How far a card reaches across tenants — a separate axis from `tenantId`,
 *  which says who owns it. 'own' = the owning tenant only (for a "0"-owned card
 *  that means direct-to-consumer users, not the schools); 'all' = every tenant,
 *  and only a "0"-owned card may be 'all'. Absent on cards written before the
 *  field existed, which reached everyone. */
type TenantScope = 'own' | 'all';

/** The public (direct-to-consumer) tenant, and the only owner allowed to reach
 *  every other tenant. Mirrors TENANT_ID_PUBLIC on the backend. */
const PUBLIC_TENANT = '0';

interface WhatsNewTip {
  id: string;
  tenantId?: string;
  tenantScope?: TenantScope;
  appTarget: AppTarget;
  title: string;
  /** Absent ⇒ true; a hidden header used to be an empty title. */
  showTitle?: boolean;
  body: string;
  imageUrl?: string;
  imageFit?: ImageFit;
  presentation?: Presentation;
  priority: number;
  targetRings?: Ring[];
  targetExams?: string[];
  targetPlatforms?: Platform[];
  minVersion?: { web?: string; ios?: string; android?: string };
  platformOverrides?: { web?: PlatformOverride; ios?: PlatformOverride; android?: PlatformOverride };
  condition?: SubjectSolveCondition;
  experienceKey?: string;
  experienceData?: Record<string, unknown>;
  afterDismissDays: number;
  afterRemindLaterDays: number;
  publishAt: string;
  expireAt?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  /** List responses only: the sole targeted user on a `selected` card whose
   *  audience is exactly one. Absent otherwise. */
  singleTargetLabel?: string;
}

interface FormData {
  tenantScope: TenantScope;
  appTarget: AppTarget;
  title: string;
  showTitle: boolean;
  body: string;
  imageUrl: string;
  imageFit: ImageFit;
  presentation: Presentation;
  priority: string;
  targetRings: Ring[];
  targetExams: string;
  targetPlatforms: Platform[];
  minVersionWeb: string;
  minVersionIos: string;
  minVersionAndroid: string;
  overrideWebTitle: string;
  overrideWebBody: string;
  overrideIosTitle: string;
  overrideIosBody: string;
  overrideAndroidTitle: string;
  overrideAndroidBody: string;
  conditionEnabled: boolean;
  conditionTargetSubject: Subject;
  conditionTargetMinAttempted: string;
  conditionExcludeSubject: Subject;
  conditionExcludeMaxAttempted: string;
  conditionWindowDays: string;
  experienceKey: string;
  experienceDataJson: string;
  afterDismissDays: string;
  afterRemindLaterDays: string;
  publishAt: string;
  expireAt: string;
}

// Registered custom-experience keys (Tier C). Static list, versioned with the app;
// must stay in sync with the client registrations in each app's whatsNewExperiences/.
// '' = default title/body card. See docs/FeatureDesigns/whats-new-custom-experiences.md.
const EXPERIENCE_KEYS: { key: string; label: string }[] = [
  { key: '', label: 'Default card (title / body / image)' },
  { key: 'quick-poll', label: 'quick-poll (student) — one-tap poll' },
  { key: 'survey', label: 'survey (student) — multi-question survey with results' },
  { key: 'chapter-progress', label: 'chapter-progress (student) — per-chapter stats + practise nudge' },
  { key: 'assignment-report', label: 'assignment-report (student) — latest AI report; auto-skips users with none' },
];

/** Experience keys the SERVER can build a payload for — mirrors the BUILDERS
 *  registry in functions/src/whatsNew/experiencePayloads.ts. Only these can be
 *  precomputed; anything else has no server-side data to warm. */
const PRECOMPUTABLE_EXPERIENCES = new Set(['chapter-progress']);

/** Gate that decides whether precomputed payloads are actually SERVED. Warming
 *  with this off writes cache entries nobody reads, so the UI says so. */
const PRECOMPUTE_GATE = 'whats_new_experience_precompute';

interface WarmResult { scanned?: number; warmed?: number; failed?: number; truncated?: boolean }

// ── API ──────────────────────────────────────────────────────────────────

const api = {
  list: (params?: { appTarget?: string; activeOnly?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.appTarget) q.set('appTarget', params.appTarget);
    if (params?.activeOnly !== undefined) q.set('activeOnly', String(params.activeOnly));
    const qs = q.toString();
    return fetchWithAdmin<WhatsNewTip[]>(`/console/whats-new-tips${qs ? `?${qs}` : ''}`);
  },
  create: (data: Partial<WhatsNewTip>) =>
    fetchWithAdmin<WhatsNewTip>('/console/whats-new-tips', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<WhatsNewTip>) =>
    fetchWithAdmin<WhatsNewTip>(`/console/whats-new-tips/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    fetchWithAdmin<{ id: string; deleted: boolean }>(`/console/whats-new-tips/${id}`, {
      method: 'DELETE',
    }),
  stats: (id: string) => fetchWithAdmin<TipStats>(`/console/whats-new-tips/${id}/stats`),
  reach: (id: string) => fetchWithAdmin<TipReach>(`/console/whats-new-tips/${id}/reach`),
  audience: (id: string, page: number, filters?: Record<string, string>, sort?: SortState, pageSize = 25) => {
    const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    for (const key of ['search', 'status', 'ring', 'source', 'seen']) {
      if (filters?.[key]) q.set(key, filters[key]);
    }
    if (sort) { q.set('sortBy', sort.sortBy); q.set('sortOrder', sort.sortOrder); }
    return fetchWithAdmin<AudienceResponse>(`/console/whats-new-tips/${id}/audience?${q}`);
  },
  featureGates: () =>
    fetchWithAdmin<{ gates: Record<string, { enabled: boolean; enabledForRings?: string[] }> }>('/console/feature-gates'),
  asyncJob: (jobId: string) =>
    fetchWithAdmin<{ job: { id: string; status: string; result?: unknown; lastError?: { message: string } } }>(`/console/async-jobs/${jobId}`),
  warm: (id: string, scope: Record<string, unknown>) =>
    fetchWithAdmin<{ jobId: string; status: string; experienceKey: string; scope: string }>(`/console/whats-new-tips/${id}/warm`, {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
  resetView: (id: string, userId: string) =>
    fetchWithAdmin<{ userId: string; reset: boolean }>(`/console/whats-new-tips/${id}/audience/reset-view`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  setOverride: (id: string, userId: string, op: 'include' | 'exclude' | 'clear') =>
    fetchWithAdmin<{ includeUserIds: string[]; excludeUserIds: string[] }>(`/console/whats-new-tips/${id}/audience/override`, {
      method: 'POST',
      body: JSON.stringify({ userId, op }),
    }),
  bulkOverride: (id: string, op: 'remove-all' | 'readd-all') =>
    fetchWithAdmin<{ includeUserIds: string[]; excludeUserIds: string[] }>(`/console/whats-new-tips/${id}/audience/override`, {
      method: 'POST',
      body: JSON.stringify({ op }),
    }),
  // Bulk op over a specific selection (multi-select in the table).
  bulkSelection: (id: string, userIds: string[], op: 'include' | 'exclude' | 'clear') =>
    fetchWithAdmin<{ includeUserIds: string[]; excludeUserIds: string[] }>(`/console/whats-new-tips/${id}/audience/override`, {
      method: 'POST',
      body: JSON.stringify({ userIds, op }),
    }),
  searchUsers: (search: string) =>
    fetchWithAdmin<{ items: { id: string; name: string; email: string; ring?: string }[]; total: number }>(
      `/console/users?search=${encodeURIComponent(search)}&pageSize=10&includeDummies=true`),
};

interface AudienceUser {
  id: string; name?: string; email?: string; ring?: string; targetExams?: string[];
  source: 'auto' | 'manual';
  /** 'past' = has seen the tip but no longer matches its targeting. */
  status: 'targeted' | 'removed' | 'past';
  impressions?: number;
  lastAction?: string;
  lastActionAt?: string;
  lastSeenAt?: string;
  nextEligibleAt?: string;
}
interface AudienceResponse {
  items: AudienceUser[];
  /** Present when these rows are your tenant's slice of a card that reaches
   *  every tenant. Shown verbatim above the table. */
  rosterNote?: string;
  total: number; page: number; pageSize: number;
  note?: string;
  targetedTotal?: number;
  removedTotal?: number;
  pastTotal?: number;
}

// ── Effectiveness analytics (see docs/FeatureDesigns/whats-new-analytics.md) ──

type TipActionKey = 'dismiss' | 'remind-later' | 'try-now' | 'close' | 'completed' | 'none';

interface TipActionStat {
  count: number;
  avgSeconds?: number;
}

interface TipStats {
  peopleShown: number;
  totalImpressions: number;
  actedCount: number;
  avgSecondsToAction?: number;
  actions: Record<TipActionKey, TipActionStat>;
}

/** Live count of how many users currently match the tip's criteria (the addressable pool). */
interface TipReach {
  qualifying: number;
  hasCondition: boolean;
  hasTargeting: boolean;
  windowDays?: number;
  consideredUsers: number;
  note?: string;
}

const ACTION_LABELS: Record<TipActionKey, string> = {
  'completed': 'Completed',
  'try-now': 'Try Now',
  'dismiss': 'Dismiss',
  'remind-later': 'Remind later',
  'close': 'Closed',
  'none': 'No action',
};

const ACTION_ORDER: TipActionKey[] = ['completed', 'try-now', 'dismiss', 'remind-later', 'close', 'none'];

/** The most-clicked control (excluding "none"), for the compact list cell. */
function dominantAction(stats: TipStats): { key: TipActionKey; count: number } | null {
  let best: { key: TipActionKey; count: number } | null = null;
  for (const key of ACTION_ORDER) {
    if (key === 'none') continue;
    const c = stats.actions[key]?.count ?? 0;
    if (c > 0 && (!best || c > best.count)) best = { key, count: c };
  }
  return best;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateForInput(iso: string): string {
  if (!iso) return '';
  return iso.slice(0, 16); // YYYY-MM-DDTHH:mm
}

// 'none' is the transient both-unticked state; it can't be saved, so it never
// reaches the list where these labels are used.
const APP_TARGET_LABELS: Record<AppTarget, string> = { student: 'Student', teacher: 'Teacher', all: 'All', selected: 'Selected users', none: '—' };

/** Whose users this card reaches, for the list. A card with no `tenantScope`
 *  predates the field and reaches everyone. */
function tenantReachLabel(t: WhatsNewTip): string {
  if ((t.tenantScope ?? 'all') === 'all') return 'All tenants';
  const owner = t.tenantId ?? PUBLIC_TENANT;
  return owner === PUBLIC_TENANT ? 'Public only' : owner;
}

/** Sortable list columns → the value each sorts on. Client-side is right here:
 *  the endpoint returns every card and the page filters in memory, so there is
 *  no server page that sorting could disagree with.
 *
 *  Effectiveness is absent deliberately — those numbers are fetched per row by
 *  TipEffectivenessCell, so the list has nothing to compare. */
const TIP_SORT_VALUES: Record<string, (t: WhatsNewTip) => string | number> = {
  title: t => (t.title || '').toLowerCase(),
  target: t => APP_TARGET_LABELS[t.appTarget] ?? '',
  tenants: t => tenantReachLabel(t).toLowerCase(),
  priority: t => t.priority ?? 0,
  platforms: t => (t.targetPlatforms?.length ? [...t.targetPlatforms].sort().join(',') : 'all'),
  publish: t => Date.parse(t.publishAt) || 0,
  status: t => (t.isActive ? 1 : 0),
};

type TipSortKey = keyof typeof TIP_SORT_VALUES;
interface TipSort { key: TipSortKey; dir: 'asc' | 'desc' }

function sortTips(tips: WhatsNewTip[], sort: TipSort | null): WhatsNewTip[] {
  if (!sort) return tips;
  const valueOf = TIP_SORT_VALUES[sort.key];
  if (!valueOf) return tips;
  const factor = sort.dir === 'asc' ? 1 : -1;
  // Copy: the query's array is cached by react-query and must not be reordered.
  return [...tips].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (av === bv) return 0;
    return (typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv))) * factor;
  });
}

const PLATFORMS: Platform[] = ['ios', 'android', 'web'];
const RINGS: Ring[] = ['devtesting', 'beta', 'prod', 'guest'];

function emptyForm(): FormData {
  return {
    // Own tenant by default: reaching every tenant is a deliberate choice, not
    // what an author gets by leaving a control alone.
    tenantScope: 'own',
    appTarget: 'all',
    title: '',
    showTitle: true,
    body: '',
    imageUrl: '',
    imageFit: 'contain',
    presentation: 'default',
    priority: '0',
    targetRings: [],
    targetExams: '',
    targetPlatforms: [],
    minVersionWeb: '',
    minVersionIos: '',
    minVersionAndroid: '',
    overrideWebTitle: '',
    overrideWebBody: '',
    overrideIosTitle: '',
    overrideIosBody: '',
    overrideAndroidTitle: '',
    overrideAndroidBody: '',
    conditionEnabled: false,
    conditionTargetSubject: 'chemistry',
    conditionTargetMinAttempted: '25',
    conditionExcludeSubject: 'physics',
    conditionExcludeMaxAttempted: '0',
    conditionWindowDays: '30',
    experienceKey: '',
    experienceDataJson: '',
    afterDismissDays: '30',
    afterRemindLaterDays: '7',
    publishAt: new Date().toISOString().slice(0, 16),
    expireAt: '',
  };
}

function toForm(t: WhatsNewTip): FormData {
  return {
    // Absent means the card predates the field and reaches everyone — show that
    // truthfully rather than defaulting it to 'own' and silently narrowing it.
    tenantScope: t.tenantScope ?? 'all',
    appTarget: t.appTarget,
    title: t.title,
    showTitle: t.showTitle !== false,
    body: t.body,
    imageUrl: t.imageUrl || '',
    imageFit: t.imageFit || 'contain',
    presentation: t.presentation || 'default',
    priority: String(t.priority),
    targetRings: t.targetRings || [],
    targetExams: (t.targetExams || []).join(', '),
    targetPlatforms: t.targetPlatforms || [],
    minVersionWeb: t.minVersion?.web || '',
    minVersionIos: t.minVersion?.ios || '',
    minVersionAndroid: t.minVersion?.android || '',
    overrideWebTitle: t.platformOverrides?.web?.title || '',
    overrideWebBody: t.platformOverrides?.web?.body || '',
    overrideIosTitle: t.platformOverrides?.ios?.title || '',
    overrideIosBody: t.platformOverrides?.ios?.body || '',
    overrideAndroidTitle: t.platformOverrides?.android?.title || '',
    overrideAndroidBody: t.platformOverrides?.android?.body || '',
    conditionEnabled: !!t.condition,
    conditionTargetSubject: (t.condition?.targetSubject as Subject) || 'chemistry',
    conditionTargetMinAttempted: String(t.condition?.targetMinAttempted ?? 25),
    conditionExcludeSubject: (t.condition?.excludeSubject as Subject) || 'physics',
    conditionExcludeMaxAttempted: String(t.condition?.excludeMaxAttempted ?? 0),
    conditionWindowDays: String(t.condition?.windowDays ?? 30),
    experienceKey: t.experienceKey ?? '',
    experienceDataJson: t.experienceData ? JSON.stringify(t.experienceData, null, 2) : '',
    afterDismissDays: String(t.afterDismissDays ?? 30),
    afterRemindLaterDays: String(t.afterRemindLaterDays ?? 7),
    publishAt: formatDateForInput(t.publishAt),
    expireAt: t.expireAt ? formatDateForInput(t.expireAt) : '',
  };
}

function buildMinVersion(form: FormData): WhatsNewTip['minVersion'] {
  const mv: Record<string, string> = {};
  if (form.minVersionWeb) mv.web = form.minVersionWeb;
  if (form.minVersionIos) mv.ios = form.minVersionIos;
  if (form.minVersionAndroid) mv.android = form.minVersionAndroid;
  return Object.keys(mv).length > 0 ? mv : undefined;
}

function buildPlatformOverrides(form: FormData): WhatsNewTip['platformOverrides'] {
  const po: Record<string, PlatformOverride> = {};
  if (form.overrideWebTitle || form.overrideWebBody) po.web = { title: form.overrideWebTitle || undefined, body: form.overrideWebBody || undefined };
  if (form.overrideIosTitle || form.overrideIosBody) po.ios = { title: form.overrideIosTitle || undefined, body: form.overrideIosBody || undefined };
  if (form.overrideAndroidTitle || form.overrideAndroidBody) po.android = { title: form.overrideAndroidTitle || undefined, body: form.overrideAndroidBody || undefined };
  return Object.keys(po).length > 0 ? po : undefined;
}

function buildCondition(form: FormData): SubjectSolveCondition | undefined {
  if (!form.conditionEnabled) return undefined;
  return {
    type: 'subject-daily-solve',
    targetSubject: form.conditionTargetSubject,
    targetMinAttempted: parseInt(form.conditionTargetMinAttempted, 10) || 0,
    excludeSubject: form.conditionExcludeSubject,
    excludeMaxAttempted: parseInt(form.conditionExcludeMaxAttempted, 10) || 0,
    windowDays: parseInt(form.conditionWindowDays, 10) || 30,
  };
}

// Subjects for which the student app wires a "Try Now" deep link off `excludeSubject`.
const TRY_NOW_LINKED_SUBJECTS: Subject[] = ['maths', 'physics', 'chemistry'];

function summarizeCondition(form: FormData): string {
  const minAttempted = parseInt(form.conditionTargetMinAttempted, 10) || 0;
  const maxAttempted = parseInt(form.conditionExcludeMaxAttempted, 10) || 0;
  const windowDays = parseInt(form.conditionWindowDays, 10) || 30;
  const { conditionTargetSubject: targetSubject, conditionExcludeSubject: excludeSubject } = form;
  const tryNowClause = TRY_NOW_LINKED_SUBJECTS.includes(excludeSubject)
    ? `and its "Try Now" button opens the ${excludeSubject} practice page`
    : `("Try Now" has no link configured for ${excludeSubject})`;
  return `Shown when the student has attempted ≥${minAttempted} ${targetSubject} and ≤${maxAttempted} ${excludeSubject} questions in the last ${windowDays} days, ${tryNowClause}.`;
}

/** Custom-experience fields. Throws on invalid experienceData JSON so save is blocked with a clear error. */
/** Lenient parse of the experience-data JSON for the preview (invalid → {}). */
function parsePreviewData(json: string): Record<string, unknown> {
  const raw = json.trim();
  if (!raw) return {};
  try {
    const d = JSON.parse(raw);
    return d && typeof d === 'object' && !Array.isArray(d) ? d as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function buildExperience(form: FormData): Pick<WhatsNewTip, 'experienceKey' | 'experienceData'> {
  const key = form.experienceKey.trim();
  if (!key) return { experienceKey: undefined, experienceData: undefined };
  let data: Record<string, unknown> | undefined;
  const raw = form.experienceDataJson.trim();
  if (raw) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new Error('Experience data is not valid JSON');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Experience data must be a JSON object');
    }
    data = parsed as Record<string, unknown>;
  }
  if (key === 'survey') validateSurveyDefinition(data);
  return { experienceKey: key, experienceData: data };
}

/** Client-side guardrails so a malformed survey can't be saved (the server
 *  re-validates on submit). Mirrors SurveyDefinitionSchema's core rules. */
function validateSurveyDefinition(data: Record<string, unknown> | undefined): void {
  const questions = (data as { questions?: unknown } | undefined)?.questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    throw new Error('A survey needs at least one question');
  }
  const ids = new Set<string>();
  questions.forEach((raw, i) => {
    const q = raw as { id?: string; prompt?: string; type?: string; options?: { value?: string }[]; scale?: { min?: number; max?: number } };
    if (!q.id) throw new Error(`Question ${i + 1} is missing an id`);
    if (ids.has(q.id)) throw new Error(`Duplicate question id "${q.id}"`);
    ids.add(q.id);
    if (!q.prompt || !q.prompt.trim()) throw new Error(`Question ${i + 1} needs a prompt`);
    if (q.type === 'single' || q.type === 'multi') {
      const opts = q.options ?? [];
      if (opts.length < 2) throw new Error(`"${q.prompt}" needs at least 2 options`);
      const vals = opts.map(o => o.value);
      if (vals.some(v => !v)) throw new Error(`"${q.prompt}" has an option with no value`);
      if (new Set(vals).size !== vals.length) throw new Error(`"${q.prompt}" has duplicate option values`);
    }
    if (q.type === 'rating') {
      if (!q.scale || Number(q.scale.min) >= Number(q.scale.max)) throw new Error(`"${q.prompt}" rating needs min < max`);
    }
  });
}

function buildReappear(form: FormData): Pick<WhatsNewTip, 'afterDismissDays' | 'afterRemindLaterDays'> {
  return {
    afterDismissDays: parseInt(form.afterDismissDays, 10) || 30,
    afterRemindLaterDays: parseInt(form.afterRemindLaterDays, 10) || 7,
  };
}

function fromForm(form: FormData): Partial<WhatsNewTip> {
  const split = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);
  const exams = split(form.targetExams);
  // A hand-picked audience overrides the criteria rather than combining with
  // them, so the criteria fields are left out on save. The server strips them
  // too — this only keeps the request honest about what was authored.
  const pickedAudience = form.appTarget === 'selected';
  return {
    // The server keeps the last word: 'all' survives only on a card owned by the
    // public tenant, whatever this sends.
    tenantScope: form.tenantScope,
    appTarget: form.appTarget,
    title: form.title,
    showTitle: form.showTitle,
    body: form.body,
    imageUrl: form.imageUrl || undefined,
    imageFit: form.imageFit,
    presentation: form.presentation,
    priority: parseInt(form.priority, 10) || 0,
    targetRings: !pickedAudience && form.targetRings.length > 0 ? form.targetRings : undefined,
    targetExams: !pickedAudience && exams.length > 0 ? exams : undefined,
    targetPlatforms: form.targetPlatforms.length > 0 ? form.targetPlatforms : undefined,
    minVersion: buildMinVersion(form),
    platformOverrides: buildPlatformOverrides(form),
    condition: pickedAudience ? undefined : buildCondition(form),
    ...buildExperience(form),
    ...buildReappear(form),
    publishAt: form.publishAt ? new Date(form.publishAt).toISOString() : new Date().toISOString(),
    expireAt: form.expireAt ? new Date(form.expireAt).toISOString() : undefined,
  };
}

// ── Component ────────────────────────────────────────────────────────────

/** Compact per-row effectiveness summary, lazy-loaded per visible tip. */
function TipEffectivenessCell({ tipId }: { tipId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['whats-new-tip-stats', tipId],
    queryFn: () => api.stats(tipId),
    staleTime: 60_000,
  });

  if (isLoading) return <span className="wnt-eff-muted">…</span>;
  if (error) return <span className="wnt-eff-muted">—</span>;
  if (!data || data.peopleShown === 0) return <span className="wnt-eff-muted">Not shown yet</span>;

  const top = dominantAction(data);
  const rate = data.peopleShown > 0 ? Math.round((data.actedCount / data.peopleShown) * 100) : 0;
  return (
    <div className="wnt-eff">
      <span className="wnt-eff-shown">👁 {data.peopleShown} shown</span>
      {top && (
        <span className="wnt-eff-top">
          {ACTION_LABELS[top.key]} {Math.round((top.count / data.peopleShown) * 100)}%
        </span>
      )}
      <span className="wnt-eff-time">
        {data.avgSecondsToAction != null ? `~${data.avgSecondsToAction}s to act` : `${rate}% acted`}
      </span>
    </div>
  );
}

/** Full effectiveness breakdown, shown in the tip's edit/detail view. */
function TipEffectivenessPanel({ tipId }: { tipId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['whats-new-tip-stats', tipId],
    queryFn: () => api.stats(tipId),
    staleTime: 60_000,
  });
  // Reach is a live cross-user scan, so fetch it separately from the lightweight stats.
  const reachQ = useQuery({
    queryKey: ['whats-new-tip-reach', tipId],
    queryFn: () => api.reach(tipId),
    staleTime: 60_000,
  });

  if (isLoading) return <p className="wnt-hint">Loading effectiveness…</p>;
  if (error) return <p className="wnt-hint">Couldn't load effectiveness.</p>;
  if (!data) return null;

  const reachLabel = reachQ.data
    ? (reachQ.data.hasCondition
        ? `qualify now (last ${reachQ.data.windowDays}d)`
        : reachQ.data.hasTargeting ? 'match targeting' : 'all users')
    : 'qualify now';

  return (
    <div className="wnt-eff-panel">
      <div className="wnt-eff-headline">
        <div title={reachQ.data?.note || 'How many users currently match this tip’s criteria.'}>
          <span className="wnt-eff-num">{reachQ.isLoading ? '…' : reachQ.error ? '—' : reachQ.data?.qualifying ?? '—'}</span>
          <span className="wnt-eff-lbl">{reachLabel}</span>
        </div>
        <div><span className="wnt-eff-num">{data.peopleShown}</span><span className="wnt-eff-lbl">people shown</span></div>
        <div><span className="wnt-eff-num">{data.totalImpressions}</span><span className="wnt-eff-lbl">impressions</span></div>
        <div><span className="wnt-eff-num">{data.actedCount}</span><span className="wnt-eff-lbl">acted</span></div>
        <div><span className="wnt-eff-num">{data.avgSecondsToAction != null ? `${data.avgSecondsToAction}s` : '—'}</span><span className="wnt-eff-lbl">avg time to act</span></div>
      </div>
      {data.peopleShown === 0 ? (
        <p className="wnt-hint">No one has been shown this tip yet.</p>
      ) : (
        <table className="wnt-eff-table">
          <thead>
            <tr><th>Where they clicked</th><th>People</th><th>Share</th><th>Avg time</th></tr>
          </thead>
          <tbody>
            {ACTION_ORDER.map(key => {
              const s = data.actions[key];
              const count = s?.count ?? 0;
              if (count === 0) return null;
              return (
                <tr key={key}>
                  <td>{ACTION_LABELS[key]}</td>
                  <td>{count}</td>
                  <td>{Math.round((count / data.peopleShown) * 100)}%</td>
                  <td>{key !== 'none' && s?.avgSeconds != null ? `${s.avgSeconds}s` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Audience management: everyone the tip touches — targeted, removed, or already
 *  shown — in one table, with add/remove overrides. "Has seen it" is a filter
 *  here rather than a separate view, so every row carries the same columns. */
function TipAudienceTab({ tipId, appTarget, experienceKey, experienceData }: { tipId: string; appTarget: AppTarget; experienceKey?: string; experienceData?: Record<string, unknown> }) {
  // A hand-picked card has no criteria audience at all, so this tab is not a
  // set of adjustments to a computed list — it IS the list.
  const pickedAudience = appTarget === 'selected';
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  // Committed filter values (search/status/ring/source/seen) — SearchFilterBar
  // drafts until Fetch. Server-side, so they apply across the whole audience.
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  // Sorting is server-side for the same reason paging is: sorting the 25 rows
  // the client happens to hold would not sort the audience.
  const [sort, setSort] = useState<SortState | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [previewUser, setPreviewUser] = useState<UserSearchOption | null>(null);

  const audienceQ = useQuery({
    queryKey: ['wnt-audience', tipId, page, filterValues, sort],
    queryFn: () => api.audience(tipId, page, filterValues, sort),
  });

  const overrideMut = useMutation({
    mutationFn: ({ userId, op }: { userId: string; op: 'include' | 'exclude' | 'clear' }) => api.setOverride(tipId, userId, op),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wnt-audience', tipId] });
      qc.invalidateQueries({ queryKey: ['whats-new-tip-reach', tipId] });
    },
  });

  // Only "re-add all" is offered: clearing every removal is recoverable, while
  // a one-click "remove all" over the whole audience is not worth its blast
  // radius — bulk removal goes through the row multi-select instead.
  const bulkMut = useMutation({
    mutationFn: (op: 'readd-all') => api.bulkOverride(tipId, op),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wnt-audience', tipId] });
      qc.invalidateQueries({ queryKey: ['whats-new-tip-reach', tipId] });
    },
  });

  // Bulk add/remove over the multi-selected rows.
  // "Only these users" — the action an admin actually wants when they mean
  // "show this to one person". Excluding everyone else instead is a SNAPSHOT of
  // who exists today: a card narrowed that way still names 'student' as its
  // rule, so every user who registers afterwards qualifies and is served it.
  // That is how a personal note addressed to one student reached strangers.
  // Flipping appTarget to 'selected' removes the criteria audience entirely, so
  // the include list is the whole audience, now and later.
  const onlyTheseMut = useMutation({
    mutationFn: async (userIds: string[]) => {
      await api.update(tipId, { appTarget: 'selected', excludeUserIds: [] } as Partial<WhatsNewTip>);
      return api.bulkSelection(tipId, userIds, 'include');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whats-new-tip-audience', tipId] });
      qc.invalidateQueries({ queryKey: ['whats-new-tips'] });
      setSelectedIds(new Set());
    },
    onError: (e) => alert(e instanceof Error ? e.message : 'Could not pin this audience'),
  });

  const bulkSelMut = useMutation({
    mutationFn: ({ userIds, op }: { userIds: string[]; op: 'include' | 'exclude' | 'clear' }) => api.bulkSelection(tipId, userIds, op),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wnt-audience', tipId] });
      qc.invalidateQueries({ queryKey: ['whats-new-tip-reach', tipId] });
      setSelectedIds(new Set());
    },
  });

  // Forget a user's view so the tip can reach them again — re-testing a tip
  // end-to-end, or giving someone another look at one they dismissed too fast.
  const resetViewMut = useMutation({
    mutationFn: (userId: string) => api.resetView(tipId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wnt-audience', tipId] });
      qc.invalidateQueries({ queryKey: ['whats-new-tip-stats', tipId] });
    },
  });

  // Precompute this tip's experience data for a cohort, so the card opens with
  // no build at all. Only offered for experiences the server can build — see
  // docs/FeatureDesigns/whats-new-experience-precompute.md.
  const canWarm = PRECOMPUTABLE_EXPERIENCES.has(experienceKey ?? '');
  const [warmScope, setWarmScope] = useState('ring:devtesting');
  const [warmJobId, setWarmJobId] = useState<string | null>(null);

  // Whether precompute is actually served. Warming is useless while this is
  // off, and the gate lives on a different admin page — so surface it here
  // rather than letting an admin warm a cohort and see nothing change.
  const gatesQ = useQuery({ queryKey: ['wnt-feature-gates'], queryFn: () => api.featureGates() });
  const gate = gatesQ.data?.gates?.[PRECOMPUTE_GATE];
  const gateRings = gate?.enabledForRings?.length ? gate.enabledForRings.join(', ') : null;

  const warmMut = useMutation({
    mutationFn: (scope: Record<string, unknown>) => api.warm(tipId, scope),
    onSuccess: (r) => setWarmJobId(r.jobId),
  });

  const startWarm = () => {
    const [kind, value] = warmScope.split(':');
    warmMut.mutate(kind === 'ring' ? { kind, ring: value } : { kind });
  };

  // Poll the job so the row reports what actually happened, not just an id.
  const warmJobQ = useQuery({
    queryKey: ['wnt-warm-job', warmJobId],
    queryFn: () => api.asyncJob(warmJobId!),
    enabled: !!warmJobId,
    refetchInterval: (q) => {
      const st = q.state.data?.job.status;
      return st && ['completed', 'failed', 'dead'].includes(st) ? false : 2000;
    },
  });
  const warmJob = warmJobQ.data?.job;
  const warmCounts = warmJob?.result as WarmResult | undefined;

  const data = audienceQ.data;
  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const canPreview = hasPreview(experienceKey ?? '');

  // Multi-select over the current page.
  const pageIds = items.map(u => u.id);
  const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const toggleAll = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allSelected) pageIds.forEach(id => next.delete(id)); else pageIds.forEach(id => next.add(id));
    return next;
  });
  const toggleOne = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const STATUS_LABEL: Record<AudienceUser['status'], string> = {
    targeted: 'Targeted', removed: 'Removed', past: 'Not targeted',
  };

  const columns: ColumnDef<AudienceUser>[] = [
    {
      key: 'sel',
      header: <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" />,
      render: (u) => <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleOne(u.id)} aria-label={`Select ${u.email || u.id}`} />,
    },
    { key: 'user', header: 'User', sortable: true, render: (u) => <span>{u.name || u.id}<div className="wnt-hint">{u.email}</div></span> },
    { key: 'ring', header: 'Ring', sortable: true, render: (u) => u.ring || '—' },
    { key: 'exams', header: 'Exams', render: (u) => u.targetExams?.join(', ') || '—' },
    { key: 'source', header: 'Source', render: (u) => u.source === 'manual' ? <span className="wnt-badge">manual</span> : 'auto' },
    { key: 'status', header: 'Status', render: (u) => (
      <span className={`wnt-badge wnt-badge-${u.status}`}>{STATUS_LABEL[u.status]}</span>
    ) },
    // Delivery state, folded in from the tip's view records — this column is
    // what the separate "Has seen it" view used to be.
    { key: 'seen', header: 'Seen', sortable: true, render: (u) => u.impressions
      ? (
        <span>
          {u.impressions}×{u.lastAction ? <span className="wnt-hint"> · {u.lastAction}</span> : null}
          {u.lastSeenAt && <div className="wnt-hint">{new Date(u.lastSeenAt).toLocaleDateString()}</div>}
        </span>
      )
      : <span className="wnt-hint">—</span> },
    ...(canPreview ? [{ key: 'preview', header: 'Preview', render: (u: AudienceUser) => (
      <button type="button" className="btn-link" onClick={() => setPreviewUser({ userId: u.id, email: u.email ?? '', name: u.name })}>Preview</button>
    ) } as ColumnDef<AudienceUser>] : []),
    { key: 'actions', header: '', render: (u) => (
      <span className="wnt-row-actions">
        {canWarm && u.status === 'targeted' && (
          <button type="button" className="btn-link" disabled={warmMut.isPending} onClick={() => warmMut.mutate({ kind: 'users', userIds: [u.id] })}>Warm</button>
        )}
        {/* Forget a user's view so the tip can reach them again — re-testing
            end to end, or giving someone another look at one they dismissed. */}
        {!!u.impressions && (
          <button type="button" className="btn-link" disabled={resetViewMut.isPending}
            title={`Clear this user's impressions and snooze so the tip can show again${u.nextEligibleAt ? ` (snoozed until ${new Date(u.nextEligibleAt).toLocaleDateString()})` : ''}`}
            onClick={() => resetViewMut.mutate(u.id)}>Reset view</button>
        )}
        {u.status === 'removed'
          ? <button type="button" className="btn-link" disabled={overrideMut.isPending} onClick={() => overrideMut.mutate({ userId: u.id, op: 'clear' })}>Re-add</button>
          : <button type="button" className="btn-link-danger" disabled={overrideMut.isPending} onClick={() => overrideMut.mutate({ userId: u.id, op: 'exclude' })}>Remove</button>}
      </span>
    ) },
  ];

  const filterDefs: FilterDef[] = [
    { key: 'status', kind: 'select', placeholder: 'All statuses', options: [{ value: 'targeted', label: 'Targeted' }, { value: 'removed', label: 'Removed' }, { value: 'past', label: 'Not targeted' }] },
    { key: 'ring', kind: 'select', placeholder: 'All rings', options: RINGS.map(r => ({ value: r, label: r })) },
    { key: 'source', kind: 'select', placeholder: 'All sources', options: [{ value: 'auto', label: 'auto' }, { value: 'manual', label: 'manual' }] },
    { key: 'seen', kind: 'select', placeholder: 'Seen or not', options: [{ value: 'yes', label: 'Has seen it' }, { value: 'no', label: 'Not seen yet' }] },
  ];

  return (
    <div className="wnt-audience">
      {pickedAudience && (
        <p className="wnt-hint">
          This card targets selected users only — nobody sees it until they are added here.
        </p>
      )}
      {/* Set by the server when this roster is one tenant's slice of a card
          that reaches every tenant — otherwise the counts here look like they
          contradict the reach figure, which does span all tenants. */}
      {data?.rosterNote && <p className="wnt-hint">{data.rosterNote}</p>}
      {canWarm && (
        <div className="wnt-warm">
          <span className="wnt-hint">
            Precompute this card's data so it opens instantly. Safe to re-run; entries refresh themselves.
          </span>
          <div className="wnt-warm-controls">
            <select value={warmScope} onChange={e => setWarmScope(e.target.value)}>
              {RINGS.map(r => <option key={r} value={`ring:${r}`}>Ring: {r}</option>)}
              <option value="all">Everyone</option>
            </select>
            <button type="button" className="btn-secondary" disabled={warmMut.isPending} onClick={startWarm}>
              {warmMut.isPending ? 'Starting…' : 'Warm cache'}
            </button>
            {warmJob && (
              <span className="wnt-hint">
                {warmJob.status}
                {warmCounts && ` — ${warmCounts.warmed ?? 0} warmed of ${warmCounts.scanned ?? 0}`}
                {!!warmCounts?.failed && `, ${warmCounts.failed} failed`}
                {warmCounts?.truncated && ' (capped — re-run for the rest)'}
                {warmJob.lastError && ` — ${warmJob.lastError.message}`}
              </span>
            )}
            {warmMut.error && <span className="wnt-hint">{warmMut.error instanceof Error ? warmMut.error.message : 'Failed'}</span>}
          </div>
          {gatesQ.isSuccess && !gate?.enabled && (
            <span className="wnt-warn">
              Precompute is off, so warmed entries will not be served. Turn on “{PRECOMPUTE_GATE}” in Feature Gates.
            </span>
          )}
          {gate?.enabled && gateRings && (
            <span className="wnt-hint">Served only to rings: {gateRings}.</span>
          )}
        </div>
      )}

      <SearchFilterBar
        filters={filterDefs}
        values={filterValues}
        onChange={(vals) => { setFilterValues(vals); setPage(1); setSelectedIds(new Set()); }}
        searchPlaceholder="Search users by name/email…"
        trailingActions={
          <>
            <button type="button" className="btn-secondary" onClick={() => setAddOpen(true)}>+ Add user</button>
            <Pagination page={page} totalPages={totalPages} total={data?.total} onPageChange={setPage} disabled={audienceQ.isFetching} />
          </>
        }
      />

      {(data?.removedTotal ?? 0) > 0 && (
        <div className="wnt-audience-bulk">
          {/* Removals are a snapshot of who exists today; the card's rule is
              still its app target. Said here because the consequence lands
              later and silently — on whoever registers next. */}
          {!pickedAudience && (
            <span className="wnt-hint wnt-audience-warn">
              ⚠ {data?.removedTotal} user(s) removed, but this card still targets all
              {' '}{appTarget === 'all' ? 'users' : appTarget + 's'} — anyone who registers
              later will qualify and be shown it. To restrict it for good, select the
              intended recipients and use “Only these users”.
            </span>
          )}
          <button type="button" className="btn-link" disabled={bulkMut.isPending}
            onClick={() => bulkMut.mutate('readd-all')}>Re-add all</button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="wnt-bulk-bar">
          <span className="wnt-hint">{selectedIds.size} selected</span>
          <button type="button" className="btn-secondary" disabled={bulkSelMut.isPending} onClick={() => bulkSelMut.mutate({ userIds: [...selectedIds], op: 'include' })}>Add to target</button>
          <button type="button" className="btn-secondary" disabled={bulkSelMut.isPending} onClick={() => bulkSelMut.mutate({ userIds: [...selectedIds], op: 'exclude' })}>Remove</button>
          {!pickedAudience && (
            <button
              type="button"
              className="btn-secondary"
              disabled={onlyTheseMut.isPending}
              title="Make these the ONLY recipients. Removing everyone else instead only excludes today's users — anyone who joins later would still be shown this card."
              onClick={() => {
                if (!confirm(`Show this card to ONLY the ${selectedIds.size} selected user(s)?\n\nThe card's audience becomes exactly this list — users who register later will not be added.`)) return;
                onlyTheseMut.mutate([...selectedIds]);
              }}
            >
              Only these users
            </button>
          )}
          {canWarm && <button type="button" className="btn-secondary" disabled={warmMut.isPending} onClick={() => warmMut.mutate({ kind: 'users', userIds: [...selectedIds] })}>Warm</button>}
          <button type="button" className="btn-link" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={(u) => u.id}
        rowClassName={(u) => u.status === 'targeted' ? '' : 'wnt-audience-removed'}
        loading={audienceQ.isLoading}
        emptyMessage={audienceQ.error ? "Couldn't load audience." : 'No users match.'}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(1); }}
        /* No `pagination` here — the pager lives in the filter bar above, so the
           table isn't topped by a second toolbar holding a duplicate of it. */
      />

      {addOpen && (
        <Modal isOpen onClose={() => setAddOpen(false)} title="Add a user to this tip" size="md">
          <div className="wnt-add-modal">
            <ScopedUserSearchFilter
              value=""
              onChange={() => { /* onPick carries the selection */ }}
              onPick={(u) => { if (u) { overrideMut.mutate({ userId: u.userId, op: 'include' }); setAddOpen(false); } }}
              placeholder="Search by name or email…"
              excludeGuests
            />
            <p className="wnt-hint">{pickedAudience
              ? 'Adds the user to this card’s audience. Nobody else will see it.'
              : 'Adds the user as a manual (force-include) target, even if they don’t match the tip’s targeting.'}</p>
          </div>
        </Modal>
      )}

      {previewUser && canPreview && (
        <TipPreviewModal
          experienceKey={experienceKey!}
          experienceData={experienceData ?? {}}
          initialUser={previewUser}
          onClose={() => setPreviewUser(null)}
        />
      )}
    </div>
  );
}

export function WhatsNewTips() {
  const queryClient = useQueryClient();
  const [appFilter, setAppFilter] = useState<AppTarget | ''>('');
  const [stateFilter, setStateFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [editing, setEditing] = useState<WhatsNewTip | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [detailTab, setDetailTab] = useState<'settings' | 'audience' | 'results'>('settings');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Who is authoring: the tenant-reach control is theirs only if they author
  // for the public tenant. Nav visibility is coarse — this is the same
  // assignment the backend checks before it accepts 'all'.
  const authUser = authApi.getUser();
  const ownsPublicContent = !!authUser && (
    authUser.isSuperAdmin
    || (authUser.roleAssignments ?? []).some(
      a => a.scope === PUBLIC_TENANT && (a.role === 'content_admin' || a.role === 'tenant_admin'),
    )
  );

  // Fetch all tips (drafts included) so the state filter can show either. A
  // draft is isActive=false, a published tip isActive=true.
  // Null = the server's own order (newest first), which is the useful default.
  const [listSort, setListSort] = useState<TipSort | null>(null);
  const toggleSort = (key: TipSortKey) =>
    setListSort(prev => (prev?.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));

  const { data: allTips, isLoading } = useQuery({
    queryKey: ['whats-new-tips', appFilter],
    queryFn: () => api.list({
      appTarget: appFilter || undefined,
      activeOnly: false,
    }),
  });
  const filteredTips = allTips?.filter(t =>
    stateFilter === 'all' ? true : stateFilter === 'draft' ? !t.isActive : t.isActive,
  );
  const tips = filteredTips && sortTips(filteredTips, listSort);

  // For a survey being edited: parse its definition (for results labels) and
  // detect existing responses, which lock structural edits in the builder.
  const editingSurveyDef: SurveyDefinition | null =
    editing?.experienceKey === 'survey'
      && editing.experienceData
      && Array.isArray((editing.experienceData as { questions?: unknown }).questions)
      ? (editing.experienceData as unknown as SurveyDefinition)
      : null;
  const surveyResultsQ = useQuery({
    queryKey: ['whats-new-survey-results', editing?.id],
    queryFn: () => fetchWithAdmin<SurveyResultsData>(`/console/whats-new-tips/${editing!.id}/survey-results`),
    enabled: !!editing && editing.experienceKey === 'survey',
    staleTime: 60_000,
  });
  const surveyLocked = (surveyResultsQ.data?.responseCount ?? 0) > 0;

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = fromForm(form);
      return editing ? api.update(editing.id, data) : api.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whats-new-tips'] });
      setEditing(null);
      setCreating(false);
      setForm(emptyForm());
    },
  });

  // Publish / unpublish = flip the served/not-served switch (isActive).
  const publishMutation = useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      api.update(id, { isActive: publish }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whats-new-tips'] });
    },
  });

  // Permanent delete (from the detail page). Closes the detail on success.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whats-new-tips'] });
      setEditing(null);
      setCreating(false);
    },
  });

  const handleEdit = (t: WhatsNewTip) => {
    setEditing(t);
    setCreating(false);
    setForm(toForm(t));
    setDetailTab('settings');
  };

  const handleCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm(emptyForm());
    setDetailTab('settings');
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setForm(emptyForm());
  };

  // Clone a locked (already-answered) survey into a fresh draft: keep the
  // questions + targeting, drop the tip id so Save creates a new tip. The new
  // tip has no responses, so the builder unlocks for editing.
  const handleCloneSurvey = () => {
    setEditing(null);
    setCreating(true);
    setForm(prev => ({ ...prev, title: `${prev.title} (copy)` }));
    setDetailTab('settings');
  };

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ── Form view ──────────────────────────────────────────────────────
  if (creating || editing) {
    // A hand-picked audience answers "who?" on its own, so the criteria that
    // would otherwise narrow a whole app (exams, rings, recent activity) are
    // not just inert — they are hidden, rather than left looking editable.
    // Platform/version still apply: they gate delivery per device, pinned or not.
    const pickedAudience = form.appTarget === 'selected';
    // Reaching every tenant belongs to whoever authors for the public tenant —
    // and only on a card the public tenant owns. Editing a school's card, even
    // as a super-admin, is not an opportunity to broadcast it.
    const owner = editing ? (editing.tenantId ?? PUBLIC_TENANT) : (authUser?.isSuperAdmin ? PUBLIC_TENANT : authUser?.tenantId);
    const canBroadcast = owner === PUBLIC_TENANT && ownsPublicContent;
    return (
      <div className="wnt-page">
        <BackButton label="list" onClick={handleCancel} />
        <div className="wnt-detail-head">
          {/* On edit, name WHICH card this is, so the Audience tab (where no
              other field is on screen) still says what you are looking at. An
              untitled card has no name to show, so fall back to its id. */}
          <h1>
            {editing ? 'Edit card' : 'Create card'}
            {editing && <span className="wnt-detail-subject"> — {form.title || `untitled (${editing.id.slice(0, 8)})`}</span>}
          </h1>
          {editing && (
            <div className="wnt-detail-actions">
              {editing.isActive ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={publishMutation.isPending}
                  onClick={() => { if (confirm(`Unpublish "${editing.title}"? It will stop showing to users.`)) publishMutation.mutate({ id: editing.id, publish: false }, { onSuccess: () => setEditing({ ...editing, isActive: false }) }); }}
                >
                  Unpublish
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={publishMutation.isPending}
                  onClick={() => publishMutation.mutate({ id: editing.id, publish: true }, { onSuccess: () => setEditing({ ...editing, isActive: true }) })}
                >
                  Publish
                </button>
              )}
              <button
                type="button"
                className="btn-danger"
                disabled={deleteMutation.isPending}
                onClick={() => { if (confirm(`Delete "${editing.title}"? This permanently removes the tip and cannot be undone.`)) deleteMutation.mutate(editing.id); }}
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {editing && (
          <div className="wnt-tabs">
            <button type="button" className={detailTab === 'settings' ? 'wnt-tab wnt-tab-active' : 'wnt-tab'} onClick={() => setDetailTab('settings')}>Settings</button>
            <button type="button" className={detailTab === 'audience' ? 'wnt-tab wnt-tab-active' : 'wnt-tab'} onClick={() => setDetailTab('audience')}>Audience</button>
            {/* Only a survey has responses to aggregate, so the tab appears only
                where it would have something to show. */}
            {editing.experienceKey === 'survey' && (
              <button type="button" className={detailTab === 'results' ? 'wnt-tab wnt-tab-active' : 'wnt-tab'} onClick={() => setDetailTab('results')}>Survey results</button>
            )}
          </div>
        )}

        {editing && detailTab === 'results' && editing.experienceKey === 'survey' && (
          <section className="wnt-section">
            <p className="wnt-hint">Aggregated answers across all responses.</p>
            <SurveyResults tipId={editing.id} definition={editingSurveyDef} />
          </section>
        )}

        {editing && detailTab === 'audience' && <TipAudienceTab tipId={editing.id} appTarget={editing.appTarget} experienceKey={editing.experienceKey} experienceData={editing.experienceData} />}

        {(creating || detailTab === 'settings') && (<>
        <div className="wnt-form">
          {/* ══ Content ═══════════════════════════════════════════════
              The experience picker leads: it decides whether the rest of
              this section applies at all. With an experience selected the
              dialog renders that component INSTEAD of the card, so body /
              image / per-platform overrides are never shown and are hidden
              here rather than left to look editable. */}
          <section className="wnt-card">
            <h3>Content</h3>

            <div className="wnt-form-field">
              <label>Presentation Type</label>
              <select value={form.experienceKey} onChange={e => updateField('experienceKey', e.target.value)}>
                {EXPERIENCE_KEYS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>

            <div className="wnt-form-field">
              <label>
                Title{' '}
                <span className="wnt-hint">
                  {form.experienceKey
                    ? 'Names this card in the admin list — the experience renders its own heading.'
                    : 'Names this card in the admin list, and is the dialog\u2019s header bar when shown.'}
                </span>
              </label>
              <input type="text" value={form.title} onChange={e => updateField('title', e.target.value)} placeholder="e.g. Infinite Canvas" />
              {/* Naming a card and showing a header are two different jobs. An
                  untitled card had no handle in the list; this keeps the name
                  and drops only the header. */}
              {!form.experienceKey && (
                <label className="wnt-inline-check">
                  <input type="checkbox" checked={form.showTitle} onChange={e => updateField('showTitle', e.target.checked)} />
                  <span>
                    Show the title on the card{' '}
                    <span className="wnt-hint">
                      Off = a chrome-free card (full-bleed image with just a floating close button); the title still names it here.
                    </span>
                  </span>
                </label>
              )}
            </div>

            {form.experienceKey && (
              // Preview lives below, outside this block — it is offered for a
              // plain tip as well now, so keeping a copy here would render two
              // buttons and two modals off the same `previewOpen`.
              form.experienceKey === 'survey' ? (
                  <div className="wnt-form-field">
                    <label>Survey questions <span className="wnt-hint">Build the survey; answers are stored as feedback and results appear below once responses arrive.</span></label>
                    <SurveyBuilder
                      key={editing?.id ?? 'new'}
                      value={form.experienceDataJson}
                      onChange={v => updateField('experienceDataJson', v)}
                      locked={surveyLocked}
                    />
                    {surveyLocked && (
                      <button type="button" className="action-btn wnt-self-start" onClick={handleCloneSurvey}>
                        Clone as new survey
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="wnt-form-field">
                    <label>Experience data <span className="wnt-hint">JSON object passed to the component (e.g. {`{ "question": "…", "options": [...] }`}). Leave empty for none.</span></label>
                    <textarea
                      value={form.experienceDataJson}
                      onChange={e => updateField('experienceDataJson', e.target.value)}
                      placeholder={'{\n  "question": "How are we doing?"\n}'}
                      rows={5}
                      spellCheck={false}
                    />
                  </div>
                )
            )}

            {!form.experienceKey && (
              <>
                <div className="wnt-form-field">
                  <label>Body <span className="wnt-hint">Optional. Supports markdown (default for all platforms)</span></label>
                  <textarea value={form.body} onChange={e => updateField('body', e.target.value)} placeholder="Describe the feature or tip" rows={4} />
                </div>

                <div className="wnt-form-row">
                  <div className="wnt-form-field" style={{ flex: 2 }}>
                    <label>Image URL <span className="wnt-hint">Optional illustration</span></label>
                    <input type="text" value={form.imageUrl} onChange={e => updateField('imageUrl', e.target.value)} placeholder="https://..." />
                  </div>
                  <div className="wnt-form-field">
                    {/* The dialog is a fixed height either way and the image
                        scales into it whole — the fit only decides how far the
                        image reaches. */}
                    <label>Image fit <span className="wnt-hint">Nothing is cropped either way</span></label>
                    <select value={form.imageFit} onChange={e => updateField('imageFit', e.target.value as ImageFit)}>
                      <option value="contain">Contained — framed, inside the dialog's padding</option>
                      <option value="full-bleed">Full bleed — runs to the dialog's edges</option>
                    </select>
                  </div>
                </div>

                <div className="wnt-form-field">
                  {/* Same card either way — this only decides how the reader
                      gets out of it. A "Try Now" link has no footer to live in
                      on a close-only card, so it is dropped there. */}
                  <label>Presentation <span className="wnt-hint">Which controls the card carries</span></label>
                  <select value={form.presentation} onChange={e => updateField('presentation', e.target.value as Presentation)}>
                    <option value="default">Default — Dismiss + Remind me later in the footer</option>
                    <option value="close-only">Close only — no footer, just the ✕ top-right (dismisses)</option>
                  </select>
                  {form.presentation === 'close-only' && (
                    <span className="wnt-hint">
                      The ✕ is the only way out, so it snoozes on “Reappear after dismiss” — Escape and
                      clicking the backdrop do the same. No “Try Now” button on this presentation.
                    </span>
                  )}
                </div>

                <details className="wnt-section" open={!!(form.overrideWebTitle || form.overrideWebBody || form.overrideIosTitle || form.overrideIosBody || form.overrideAndroidTitle || form.overrideAndroidBody)}>
                  <summary>Platform content overrides <span className="wnt-hint">Override title/body for specific platforms. Leave empty = use default.</span></summary>
                  {(['ios', 'android', 'web'] as const).map(plat => {
                    const platLabel = plat === 'ios' ? 'iOS' : plat === 'android' ? 'Android' : 'Web';
                    const titleKey = `override${plat.charAt(0).toUpperCase() + plat.slice(1)}Title` as keyof FormData;
                    const bodyKey = `override${plat.charAt(0).toUpperCase() + plat.slice(1)}Body` as keyof FormData;
                    return (
                      <div key={plat} className="wnt-platform-override">
                        <div className="wnt-platform-label">{platLabel}</div>
                        <div className="wnt-form-row">
                          <div className="wnt-form-field">
                            <label>Title override</label>
                            <input type="text" value={form[titleKey] as string} onChange={e => updateField(titleKey, e.target.value)} placeholder={`${platLabel}-specific title`} />
                          </div>
                          <div className="wnt-form-field" style={{ flex: 2 }}>
                            <label>Body override</label>
                            <textarea value={form[bodyKey] as string} onChange={e => updateField(bodyKey, e.target.value)} placeholder={`${platLabel}-specific body`} rows={2} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </details>
              </>
            )}

            {(!form.experienceKey || hasPreview(form.experienceKey)) && (
              <div className="wnt-form-field">
                <button type="button" className="action-btn wnt-self-start" onClick={() => setPreviewOpen(true)}>
                  Preview ▸
                </button>
              </div>
            )}
            {previewOpen && (
              <TipPreviewModal
                experienceKey={form.experienceKey}
                experienceData={parsePreviewData(form.experienceDataJson)}
                card={{ title: form.showTitle ? form.title : '', body: form.body, imageUrl: form.imageUrl || undefined, imageFit: form.imageFit, presentation: form.presentation }}
                onClose={() => setPreviewOpen(false)}
              />
            )}
          </section>

          {/* ══ Target audience ═══════════════════════════════════════
              Every rule that decides WHO sees this, in one place: which
              app, which platforms/rings/exams, which app versions, and
              what the student must recently have done. */}
          <section className="wnt-card">
            <h3>Target audience <span className="wnt-hint">Leave a field empty to place no restriction on it.</span></h3>

            {/* The coarsest audience axis, above "who?": WHOSE users. Only the
                public tenant's cards can go to every tenant, so for anyone
                else this is a statement rather than a choice. */}
            <div className="wnt-form-field">
              <label>Tenants</label>
              {canBroadcast ? (
                <select value={form.tenantScope} onChange={e => updateField('tenantScope', e.target.value as TenantScope)}>
                  <option value="own">Public tenant only — direct-to-consumer users</option>
                  <option value="all">All tenants — every school and consumer user</option>
                </select>
              ) : (
                <span className="wnt-hint">Your tenant’s users only.</span>
              )}
              {canBroadcast && form.tenantScope === 'all' && (
                <span className="wnt-hint">Every school’s students and teachers see this card too.</span>
              )}
            </div>

            <div className="wnt-form-row">
              <div className="wnt-form-field">
                {/* Checkboxes rather than a select: the stored value is one of
                    four, but the question an author is actually answering is
                    "who?" — and both apps ticked reads as "both" far more
                    directly than an "All" option does. "Selected Users" sits in
                    the same group because it answers the same question, even
                    though it excludes the other two. */}
                <label>Users *</label>
                <div className="wnt-checkbox-group">
                  {APP_TARGETS.map(t => (
                    <label key={t.value} className="check-label">
                      <input
                        type="checkbox"
                        checked={appTargetIncludes(form.appTarget, t.value)}
                        onChange={() => updateField('appTarget', toggleAppTarget(form.appTarget, t.value))}
                      />
                      <span>{t.label}</span>
                    </label>
                  ))}
                </div>
                {form.appTarget === 'none' && (
                  <span className="wnt-warn">Pick at least one — a card with no audience reaches nobody.</span>
                )}
                {pickedAudience && (
                  <span className="wnt-hint">
                    Starts with nobody. {editing
                      ? 'Add each user in the Audience tab; only those users see this card.'
                      : 'Save this card, then add each user in the Audience tab; only those users see it.'}
                  </span>
                )}
              </div>
              {!pickedAudience && (
                <div className="wnt-form-field">
                  {/* Was free text, so a typo ("JEEE") silently targeted nobody.
                      Same control and the same canonical option list the question
                      filters use. Still stored comma-joined. */}
                  <label>Exams</label>
                  <MultiSelectFilter
                    label="All exams"
                    options={REGISTRABLE_PROGRAMME_OPTIONS.map(x => ({ value: x, label: x }))}
                    selected={form.targetExams.split(',').map(x => x.trim()).filter(Boolean)}
                    onChange={sel => updateField('targetExams', sel.join(', '))}
                  />
                </div>
              )}
            </div>

            <div className="wnt-form-row">
              <div className="wnt-form-field">
                <label>Platforms</label>
                <MultiSelectFilter
                  label="All platforms"
                  options={PLATFORMS.map(p => ({ value: p, label: p === 'ios' ? 'iOS' : p === 'android' ? 'Android' : 'Web' }))}
                  selected={form.targetPlatforms}
                  onChange={sel => updateField('targetPlatforms', sel as Platform[])}
                />
              </div>

              {!pickedAudience && (
                <div className="wnt-form-field">
                  <label>Rings</label>
                  <MultiSelectFilter
                    label="All rings"
                    options={RINGS.map(r => ({ value: r, label: r }))}
                    selected={form.targetRings}
                    onChange={sel => updateField('targetRings', sel as Ring[])}
                  />
                </div>
              )}
            </div>

            <details className="wnt-section" open={!!(form.minVersionWeb || form.minVersionIos || form.minVersionAndroid)}>
              <summary>Minimum app versions <span className="wnt-hint">Show only on this version or newer. Leave empty = all versions.</span></summary>
              <div className="wnt-form-row wnt-form-row-3">
                <div className="wnt-form-field">
                  <label>Web</label>
                  <input type="text" value={form.minVersionWeb} onChange={e => updateField('minVersionWeb', e.target.value)} placeholder="e.g. 1.5.0" />
                </div>
                <div className="wnt-form-field">
                  <label>iOS</label>
                  <input type="text" value={form.minVersionIos} onChange={e => updateField('minVersionIos', e.target.value)} placeholder="e.g. 2.1.0" />
                </div>
                <div className="wnt-form-field">
                  <label>Android</label>
                  <input type="text" value={form.minVersionAndroid} onChange={e => updateField('minVersionAndroid', e.target.value)} placeholder="e.g. 2.0.0" />
                </div>
              </div>
            </details>

            {!pickedAudience && (
            <details className="wnt-section" open={form.conditionEnabled}>
              <summary>Behavioral trigger <span className="wnt-hint">Optional. Show only when the student's recent activity matches (in addition to targeting).</span></summary>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={form.conditionEnabled}
                  onChange={e => updateField('conditionEnabled', e.target.checked)}
                />
                <span>Enable subject-solve-count trigger</span>
              </label>
              {form.conditionEnabled && (
                <>
                  <div className="wnt-form-row" style={{ marginTop: 8 }}>
                    <div className="wnt-form-field">
                      <label>Rolling window (days)</label>
                      <input type="number" min={1} value={form.conditionWindowDays} onChange={e => updateField('conditionWindowDays', e.target.value)} />
                    </div>
                  </div>
                  <div className="wnt-form-row wnt-form-row-3">
                    <div className="wnt-form-field">
                      <label>Subject attempted &ge;</label>
                      <select value={form.conditionTargetSubject} onChange={e => updateField('conditionTargetSubject', e.target.value as Subject)}>
                        {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="wnt-form-field">
                      <label>Min attempted</label>
                      <input type="number" min={0} value={form.conditionTargetMinAttempted} onChange={e => updateField('conditionTargetMinAttempted', e.target.value)} />
                    </div>
                    <div />
                  </div>
                  <div className="wnt-form-row wnt-form-row-3">
                    <div className="wnt-form-field">
                      <label>Subject attempted &le;</label>
                      <select value={form.conditionExcludeSubject} onChange={e => updateField('conditionExcludeSubject', e.target.value as Subject)}>
                        {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="wnt-form-field">
                      <label>Max attempted</label>
                      <input type="number" min={0} value={form.conditionExcludeMaxAttempted} onChange={e => updateField('conditionExcludeMaxAttempted', e.target.value)} />
                    </div>
                    <div />
                  </div>
                  <p className="wnt-condition-summary">{summarizeCondition(form)}</p>
                </>
              )}
            </details>
            )}
          </section>

          {/* ══ Reappear behavior ═════════════════════════════════════ */}
          <section className="wnt-card">
            <h3>Reappear behavior <span className="wnt-hint">Days before this tip can show again to a user who acted on it. 0 = never again.</span></h3>
            <div className="wnt-form-row">
              <div className="wnt-form-field">
                <label>After dismiss (days)</label>
                <input type="number" min={0} value={form.afterDismissDays} onChange={e => updateField('afterDismissDays', e.target.value)} />
              </div>
              <div className="wnt-form-field">
                <label>After remind later (days)</label>
                <input type="number" min={0} value={form.afterRemindLaterDays} onChange={e => updateField('afterRemindLaterDays', e.target.value)} />
              </div>
            </div>
          </section>

          {/* ══ Publishing ════════════════════════════════════════════ */}
          <section className="wnt-card">
            <h3>Publishing</h3>
            <div className="wnt-form-row">
              <div className="wnt-form-field">
                <label>Priority <span className="wnt-hint">Higher = shown first</span></label>
                <input type="number" value={form.priority} onChange={e => updateField('priority', e.target.value)} />
              </div>
            </div>

            <div className="wnt-form-row">
              <div className="wnt-form-field">
                <label>Publish at *</label>
                <input type="datetime-local" value={form.publishAt} onChange={e => updateField('publishAt', e.target.value)} />
              </div>
              <div className="wnt-form-field">
                <label>Expire at <span className="wnt-hint">Optional</span></label>
                <input type="datetime-local" value={form.expireAt} onChange={e => updateField('expireAt', e.target.value)} />
              </div>
            </div>

          </section>

          {editing && (
            <details className="wnt-section">
              <summary>Effectiveness <span className="wnt-hint">Impressions, where they clicked, and time-to-action.</span></summary>
              <TipEffectivenessPanel tipId={editing.id} />
            </details>
          )}

          {saveMutation.error && (
            <div className="wnt-error">{saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}</div>
          )}

          <div className="wnt-form-actions">
            <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
            <button
              className="btn-primary"
              onClick={() => saveMutation.mutate()}
              disabled={!form.publishAt || form.appTarget === 'none' || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : (editing ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
        </>)}
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────
  return (
    <div className="wnt-page">
      <div className="wnt-header">
        <h1>What's New / Tips</h1>
        <button className="btn-primary" onClick={handleCreate}>+ Create</button>
      </div>

      <div className="wnt-filters">
        <select value={appFilter} onChange={e => setAppFilter(e.target.value as AppTarget | '')}>
          <option value="">All Targets</option>
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
          <option value="all">All (both)</option>
          <option value="selected">Selected users</option>
        </select>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value as 'all' | 'published' | 'draft')}>
          <option value="all">All states</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        {tips && <span className="wnt-count">{tips.length} items</span>}
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : !tips?.length ? (
        <p className="wnt-empty">No tips found. Create one to get started.</p>
      ) : (
        <table className="dt">
          <thead>
            <tr>
              {([
                ['title', 'Title'], ['target', 'Target'], ['tenants', 'Tenants'],
                ['priority', 'Priority'], ['platforms', 'Platforms'], ['publish', 'Publish'],
              ] as const).map(([key, label]) => (
                <th
                  key={key}
                  className="dt-th--sortable"
                  onClick={() => toggleSort(key)}
                  aria-sort={listSort?.key === key ? (listSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <span className="dt-th-content">
                    {label}
                    <span className={`dt-sort-icon ${listSort?.key === key ? 'dt-sort-icon--active' : ''}`}>
                      {listSort?.key === key ? (listSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                    </span>
                  </span>
                </th>
              ))}
              {/* Not sortable: each row fetches its own numbers, so the list has
                  nothing to compare them by. */}
              <th>Effectiveness</th>
              <th
                className="dt-th--sortable"
                onClick={() => toggleSort('status')}
                aria-sort={listSort?.key === 'status' ? (listSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <span className="dt-th-content">
                  Status
                  <span className={`dt-sort-icon ${listSort?.key === 'status' ? 'dt-sort-icon--active' : ''}`}>
                    {listSort?.key === 'status' ? (listSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                  </span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tips.map(t => (
              <tr key={t.id} className="clickable-row" onClick={() => handleEdit(t)}>
                <td>
                  <div className={t.title ? 'wnt-title' : 'wnt-title wnt-title-untitled'}>
                    {t.title || 'Untitled'}
                    {t.title && t.showTitle === false && (
                      <span className="wnt-title-hidden" title="The card shows no header — this title only names it here.">hidden</span>
                    )}
                  </div>
                  <div className="wnt-body-preview">{t.body.slice(0, 80)}{t.body.length > 80 ? '...' : ''}</div>
                </td>
                <td>
                  <span className={`wnt-target wnt-target-${t.appTarget}`}>{APP_TARGET_LABELS[t.appTarget]}</span>
                  {/* Server-supplied, and only present when the audience is exactly
                      one — see withSingleTargetEmail in whatsNew/adminCrud.ts. */}
                  {t.singleTargetLabel && (
                    <div className="wnt-target-email" title={t.singleTargetLabel}>{t.singleTargetLabel}</div>
                  )}
                </td>
                <td className="wnt-tenants">{tenantReachLabel(t)}</td>
                <td className="wnt-priority">{t.priority}</td>
                <td className="wnt-platforms">
                  {t.targetPlatforms?.length ? t.targetPlatforms.join(', ') : 'All'}
                </td>
                <td className="wnt-date">{formatDate(t.publishAt)}</td>
                <td className="wnt-eff-cell" onClick={e => e.stopPropagation()}>
                  <TipEffectivenessCell tipId={t.id} />
                </td>
                <td>
                  <span className={`wnt-status ${t.isActive ? 'wnt-published' : 'wnt-draft'}`}>
                    {t.isActive ? 'Published' : 'Draft'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
